/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createAxiosInstance } from '../utils/Axios';
import { toast } from 'react-toastify';

export default function CrmPipeline() {
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'table'
  const [selectedDeal, setSelectedDeal] = useState(null);
  
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [stats, setStats] = useState({
    totalDeals: 0,
    pipelineValue: 0,
    wonDeals: 0,
    totalRevenue: 0
  });

  const stages = ['New Lead', 'Qualified', 'Proposal', 'Won'];

  // Modal States
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [newDeal, setNewDeal] = useState({ title: '', amount: '', owner: '', customer: '', stage: 'New Lead', priority: 'Medium', notes: '', date: new Date().toISOString().split('T')[0] });
  const [isSavingDeal, setIsSavingDeal] = useState(false);

  const [showEditDealModal, setShowEditDealModal] = useState(false);
  const [editDealData, setEditDealData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [showDeleteDealModal, setShowDeleteDealModal] = useState(false);
  const [deleteDealId, setDeleteDealId] = useState(null);
  const [isDeletingDeal, setIsDeletingDeal] = useState(false);

  useEffect(() => {
    const fetchDeals = async () => {
      setIsLoading(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        
        let queryParams = `?limit=100`;
        if (searchTerm) queryParams += `&search=${searchTerm}`;

        const { data } = await axiosInstance.get(`${apiUrl}/deals${queryParams}`);
        if (data && data.success) {
          setDeals(data.deals || []);
          if (data.stats) setStats(data.stats);
        }
      } catch (error) {
        console.error("Failed to fetch deals", error);
        toast.error("Failed to load deals");
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchDeals();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, refreshTrigger]);

  const handleDragStart = (e, dealId) => {
    e.dataTransfer.setData('dealId', dealId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('dealId');
    if (!dealId) return;

    // Optimistic Update
    const originalDeals = [...deals];
    setDeals(prevDeals => prevDeals.map(deal => 
      deal._id === dealId ? { ...deal, stage: targetStage } : deal
    ));

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      await axiosInstance.put(`${apiUrl}/deals/${dealId}`, { stage: targetStage });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Failed to update deal stage", error);
      toast.error("Failed to update deal stage");
      setDeals(originalDeals); // Revert on failure
    }
  };

  const moveToWon = async (dealId) => {
    if (!dealId) return;
    
    // Optimistic
    const originalDeals = [...deals];
    setDeals(prevDeals => prevDeals.map(deal => 
      deal._id === dealId ? { ...deal, stage: 'Won' } : deal
    ));
    if (selectedDeal) {
      setSelectedDeal(prev => ({ ...prev, stage: 'Won' }));
    }

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      await axiosInstance.put(`${apiUrl}/deals/${dealId}`, { stage: 'Won' });
      toast.success("Deal moved to Won!");
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Failed to update deal stage", error);
      toast.error("Failed to update deal stage");
      setDeals(originalDeals); // Revert on failure
      if (selectedDeal) {
        setSelectedDeal(originalDeals.find(d => d._id === dealId) || selectedDeal);
      }
    }
  };

  const handleSaveDeal = async () => {
    if (!newDeal.title || !newDeal.amount) {
      toast.error("Deal Title and Amount are required");
      return;
    }
    setIsSavingDeal(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.post(`${apiUrl}/deals`, newDeal);
      
      toast.success("Deal created successfully!");
      setShowAddDealModal(false);
      setNewDeal({ title: '', amount: '', owner: '', customer: '', stage: 'New Lead', priority: 'Medium', notes: '', date: new Date().toISOString().split('T')[0] });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Save failed", error);
      toast.error(error?.response?.data?.message || "Failed to create deal");
    } finally {
      setIsSavingDeal(false);
    }
  };

  const handleUpdateDeal = async () => {
    if (!editDealData.title || !editDealData.amount) {
      toast.error("Deal Title and Amount are required");
      return;
    }
    setIsSavingEdit(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.put(`${apiUrl}/deals/${editDealData._id}`, editDealData);
      
      toast.success("Deal updated successfully!");
      setShowEditDealModal(false);
      if (selectedDeal?._id === editDealData._id) {
        setSelectedDeal({ ...selectedDeal, ...editDealData });
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Update failed", error);
      toast.error(error?.response?.data?.message || "Failed to update deal");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!deleteDealId) return;
    setIsDeletingDeal(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.delete(`${apiUrl}/deals/${deleteDealId}`);
      
      toast.success("Deal deleted successfully!");
      setShowDeleteDealModal(false);
      if (selectedDeal?._id === deleteDealId) {
        setSelectedDeal(null);
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Delete failed", error);
      toast.error(error?.response?.data?.message || "Failed to delete deal");
    } finally {
      setIsDeletingDeal(false);
      setDeleteDealId(null);
    }
  };

  return (
    <div className="crm-pipeline-container" style={{ padding: '16px', backgroundColor: '#f8f9fa', minHeight: '100%', fontFamily: 'Inter, sans-serif', position: 'relative' }}>
      
      {/* Sub-Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Link to="/crm/overview" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
              Overview
            </div>
          </Link>
          <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              Leads
            </div>
          </Link>
          <Link to="/crm/contacts" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              Contacts
            </div>
          </Link>
          <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>
              Accounts
            </div>
          </Link>
          <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
            <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
            Leads
          </div>
          <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
              Tasks
            </div>
          </Link>
          <Link to="/crm/campaigns" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
              Campaigns
            </div>
          </Link>
          <Link to="/crm/integration" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 16V7a2 2 0 0 0-2-2h-4"></path><path d="M4 16V7a2 2 0 0 1 2-2h4"></path><rect x="9" y="15" width="6" height="6" rx="1"></rect><line x1="12" y1="5" x2="12" y2="15"></line></svg>
              MCP Integration
            </div>
          </Link>
        </div>
      </div>

      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0', color: '#111827', letterSpacing: '-0.5px' }}>Pipeline</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Export</button>
          <button onClick={() => setShowAddDealModal(true)} style={{ padding: '8px 20px', backgroundColor: '#5B21B6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>+ New Deal</button>
        </div>
      </div>
      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading Deals...</div>
      ) : (
        <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '16px', minHeight: '60vh' }}>
          {stages.map(stage => (
            <div key={stage} style={{ flex: '1', minWidth: '300px', backgroundColor: '#F3F4F6', borderRadius: '8px', padding: '16px' }}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage)}
            >
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                <span>{stage}</span>
                <span style={{ backgroundColor: '#E5E7EB', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{deals.filter(d => d.stage === stage).length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {deals.filter(d => d.stage === stage).map(deal => (
                  <div key={deal._id} 
                    draggable 
                    onDragStart={(e) => handleDragStart(e, deal._id)}
                    style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '6px', border: '1px solid #E5E7EB', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', cursor: 'grab' }}
                  >
                    <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px', marginBottom: '4px' }}>{deal.name}</div>
                    <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>{deal.accountName || deal.contactName || 'No contact'}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#10B981' }}>{deal.amount ? "$" + deal.amount : '-'}</span>
                      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{deal.expectedCloseDate || ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {showAddDealModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add Deal</h3>
            <input placeholder="Name" value={newDeal.name} onChange={e => setNewDeal({...newDeal, name: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Amount" type="number" value={newDeal.amount} onChange={e => setNewDeal({...newDeal, amount: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <select value={newDeal.stage} onChange={e => setNewDeal({...newDeal, stage: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }}>
              {stages.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowAddDealModal(false)} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveDeal} disabled={isSavingDeal} style={{ padding: '8px 16px', background: '#5B21B6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

</div>);}
